import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import type { ResolvedBracketGame } from "@alpha/engine";
import { bracketGamesById } from "../../../lib/bundle";
import { cx } from "../../../lib/cx";
import {
  dayKey,
  dayLabel,
  defaultDay,
  fieldsOn,
  gameDayKey,
  groupByDay,
  tournamentDays,
} from "../../../lib/gameDay";
import { venueLabel } from "../../../lib/gameLocation";
import { playableGames } from "../../../lib/tournamentPhases";
import { ScoreCard } from "../../../components/ScoreCard";
import { EmptyState } from "../../../components/ui";
import type { TournamentContext } from "./TournamentShell";

/**
 * Game day (console spec §5.5): one day at a time, one field at a time if
 * wanted, and a card per game with the two taps a scorer needs. The day and
 * the field ride in the URL — `?day=2026-05-02&field=Riverside%20·%20Field%201`
 * — so the link an organizer texts a scorer opens on their field.
 *
 * Pool and bracket games interleave by time; the card's context line tells
 * them apart.
 */
export function Scores() {
  const { tid, bundle, caps, now } = useOutletContext<TournamentContext>();
  const [params, setParams] = useSearchParams();
  const tz = bundle.tournament.timezone;

  const days = tournamentDays(bundle.tournament, bundle.games);
  const byDay = groupByDay(bundle.games, tz);
  const playableDays = new Set(playableGames(bundle).map((g) => gameDayKey(g, tz)));
  const today = dayKey(now.toISOString(), tz) ?? "";
  const requested = params.get("day");
  const day = requested && days.includes(requested) ? requested : defaultDay(days, playableDays, today);

  const games = day ? (byDay.get(day) ?? []) : [];
  const fields = fieldsOn(games, bundle.venues);
  const requestedField = params.get("field");
  const field = requestedField && fields.includes(requestedField) ? requestedField : null;
  const shown = field ? games.filter((g) => venueLabel(bundle.venues, g) === field) : games;

  // Resolve each bracket once per render, not once per card.
  const resolved = new Map<string, ResolvedBracketGame>();
  for (const d of bundle.divisions) {
    if (bundle.games.some((g) => g.divisionId === d.id && g.type === "bracket")) {
      for (const [id, g] of bracketGamesById(bundle, d.id)) resolved.set(id, g);
    }
  }

  if (bundle.games.length === 0 || !day) {
    return (
      <EmptyState
        title="No games yet"
        hint={
          caps.schedule
            ? "The schedule is built under Schedule."
            : "An organizer builds the schedule."
        }
      />
    );
  }

  const pickField = (next: string | null) => {
    const search = new URLSearchParams({ day });
    if (next) search.set("field", next);
    setParams(search, { replace: true });
  };

  return (
    <div className="space-y-4">
      {/* Sticky under the site header, so a twenty-game Saturday can be
          scrolled without losing the way to another day or field. Bleeds to
          the page gutter so cards do not show past its edges on a phone. */}
      <div className="sticky top-14 z-30 -mx-4 bg-slate-50 px-4 py-2 sm:top-16 sm:mx-0 sm:px-0">
        <nav aria-label="Game days" className="flex gap-1 overflow-x-auto pb-1">
          {days.map((d) => {
            const current = d === day;
            return (
              <Link
                key={d}
                to={`?day=${encodeURIComponent(d)}`}
                aria-current={current ? "date" : undefined}
                className={cx(
                  "flex shrink-0 items-baseline gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600",
                  current ? "bg-navy-950 text-white" : "text-slate-600 hover:bg-slate-200/70 hover:text-navy-950",
                )}
              >
                {dayLabel(d)}
                <span className={cx("text-xs tabular-nums", current ? "text-navy-300" : "text-slate-400")}>
                  {byDay.get(d)?.length ?? 0}
                </span>
              </Link>
            );
          })}
        </nav>
        {fields.length > 1 ? (
          <div role="group" aria-label="Fields" className="mt-1.5 flex gap-1.5 overflow-x-auto pb-1">
            <FieldChip label="All fields" on={field === null} onClick={() => pickField(null)} />
            {fields.map((f) => (
              <FieldChip key={f} label={f} on={field === f} onClick={() => pickField(f)} />
            ))}
          </div>
        ) : null}
      </div>

      {shown.length === 0 ? (
        <EmptyState title={`No games on ${dayLabel(day)}`} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {shown.map((g) => (
            <ScoreCard
              key={g.id}
              tid={tid}
              bundle={bundle}
              game={g}
              resolved={resolved.get(g.id)}
              caps={caps}
              now={now}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** The same chip the event form uses for venues, so "picked" reads the same across the console. */
function FieldChip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cx(
        "shrink-0 rounded-lg border px-3 py-1.5 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600",
        on
          ? "border-brand-600 bg-brand-50 font-medium text-brandText"
          : "border-slate-300 bg-white text-slate-600 hover:border-slate-400",
      )}
    >
      {label}
    </button>
  );
}
